import i18next from 'i18next';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { fullAccept } from '../components/message-input/message-input.types';
import { type Attachment, AudioAttachment, FileAttachment, GifAttachment, MediaAttachment } from '../models/attachment';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 1024 * 1024 * 200; // 200MB

// Get accepted MIME types from fullAccept config
const acceptedMimeTypes = Object.keys(fullAccept);

/**
 * Hook for handling file paste from clipboard
 * Supports images and documents based on fullAccept config
 */
export const usePasteFiles = (
  attachments: Attachment[],
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
) => {
  const handlePaste = useCallback(
    async (event: React.ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData?.items) return;

      const files: File[] = [];

      // Extract files from clipboard items
      for (const item of Array.from(clipboardData.items)) {
        if (item.kind !== 'file') continue;

        const file = item.getAsFile();
        if (!file) continue;

        // Check file type is accepted
        const isAccepted = acceptedMimeTypes.some((mime) => {
          // Handle wildcard patterns like image/* or exact matches
          if (mime.endsWith('/*')) {
            return file.type.startsWith(mime.replace('/*', '/'));
          }
          return file.type === mime;
        });

        if (!isAccepted) {
          toast.warning(i18next.t('messages.upload.unsupportedFileType', { fileName: file.name }));
          continue;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          toast.error(i18next.t('messages.upload.fileTooLarge'));
          continue;
        }

        files.push(file);
      }

      if (files.length === 0) return;

      // Check max files limit
      if (attachments.length + files.length > MAX_FILES) {
        toast.error(i18next.t('messages.upload.maxFilesExceeded', { maxFiles: MAX_FILES }));
        return;
      }

      // Prevent default only if we have files to handle
      event.preventDefault();

      // Convert files to attachments (filter duplicates)
      const newAttachments: Attachment[] = files
        .filter(
          (f) =>
            !attachments.some(
              (existingFile) => existingFile.file?.name === f.name && existingFile.file?.lastModified === f.lastModified
            )
        )
        .map((f: File) => {
          switch (f.type) {
            case 'audio/mpeg':
              return new AudioAttachment(f);
            case 'image/gif':
              return new GifAttachment(f);
            default:
              if (f.type.startsWith('video/') || f.type.startsWith('image/')) {
                return new MediaAttachment(f);
              }
              return new FileAttachment(f);
          }
        });

      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
        toast.success(
          i18next.t('messages.upload.filesPasted', {
            count: newAttachments.length,
            defaultValue: `${newAttachments.length} file(s) added`
          })
        );
      }
    },
    [attachments, setAttachments]
  );

  return { handlePaste };
};
